import { OptionLabelValueSchema } from 'screens/LetUsHelp/utils/schemas';
import {
  LETTERS_SPACES_SPECIAL_CHARACTERS_ERROR_LABEL,
  VALID_PHONE_ERROR_LABEL,
} from 'shared/constants/error-labels';
import {
  LETTERS_SPACES_HYPENS_APOSTROPHE_REGEXP,
  LETTERS_SPACES_SPECIAL_CHARACTERS,
  NUMBERS_LETTERS_HYPHENS_SPACES_REGEXP,
  VALID_PHONE_NUMBER,
} from 'shared/constants/validation-regex-constants';
import { nullToUndefined } from 'shared/util/schema-utils';
import { z } from 'zod';

// cart location schema
export const AccessLocationSchema = z.object({
  address_line_1: z.string().transform(nullToUndefined).nullish(),
  address_line_2: z
    .string()
    .trim()
    .max(100, 'Only 100 characters allowed')
    .refine((val) => !val || LETTERS_SPACES_SPECIAL_CHARACTERS.test(val), {
      message: LETTERS_SPACES_SPECIAL_CHARACTERS_ERROR_LABEL,
    })
    .transform(nullToUndefined)
    .nullish(),
  street: z.string().transform(nullToUndefined).nullish(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  macnum: z.string().transform(nullToUndefined).nullish(),
  name: z.string().transform(nullToUndefined).nullish(),
  parent_macnum: z.string().transform(nullToUndefined).nullish(),
  parent_name: z.string().transform(nullToUndefined).nullish(),
  id: z.string().transform(nullToUndefined).nullish(),
});

export type AccessLocation = z.infer<typeof AccessLocationSchema>;

export const CreateChildAccountSchema = z.object({
  parent_account: OptionLabelValueSchema.optional(),
  location_name: z.string().trim().transform(nullToUndefined).nullish(),
  billing_type: OptionLabelValueSchema.optional(),
  invoice_delivery_method: OptionLabelValueSchema.optional(),
  invoice_style: OptionLabelValueSchema.optional(),
  location_contact_email: z
    .string()
    .trim()
    .transform(nullToUndefined)
    .nullish(),
});

const speedOptions = ['Standard', 'TwoBusinessDay', 'Overnight'] as const;

export const SpeedEnum = z.enum(speedOptions);
export type Speed = z.infer<typeof SpeedEnum>;

export const AddressSchema = z
  .object({
    // site_name: z.string().nonempty('Required'),
    // macnum: z.string().nonempty('Required'),
    business_name: z
      .string()
      .trim()
      .nonempty('Required')
      .max(100, 'Only 100 characters allowed')
      .regex(
        NUMBERS_LETTERS_HYPHENS_SPACES_REGEXP,
        'Only letters, numbers, spaces, and hyphens are allowed',
      ),
    local_contact_name: z
      .string()
      .trim()
      .nonempty('Required')
      .max(100, 'Only 100 characters allowed')
      .regex(
        LETTERS_SPACES_HYPENS_APOSTROPHE_REGEXP,
        'Only letters, spaces, hyphens, dashes and apostrophe are allowed',
      ),
    local_contact_phone: z
      .string()
      .trim()
      .nonempty('Required')
      .regex(VALID_PHONE_NUMBER, VALID_PHONE_ERROR_LABEL),
    address_line_2: z
      .string()
      .trim()
      .max(100, 'Only 100 characters allowed')
      .refine((val) => !val || LETTERS_SPACES_SPECIAL_CHARACTERS.test(val), {
        message: LETTERS_SPACES_SPECIAL_CHARACTERS_ERROR_LABEL,
      })
      .transform(nullToUndefined)
      .nullish(),
    speed: SpeedEnum.optional(),
    needsShipping: z.boolean().optional(),
  })
  .merge(AccessLocationSchema)
  .merge(CreateChildAccountSchema)
  .superRefine((data, ctx) => {
    if (!data.macnum) {
      // If cw_site_id is missing, these fields should be required
      if (!data.parent_account) {
        ctx.addIssue({
          path: ['parent_account'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.location_name) {
        ctx.addIssue({
          path: ['location_name'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.billing_type) {
        ctx.addIssue({
          path: ['billing_type'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.invoice_delivery_method) {
        ctx.addIssue({
          path: ['invoice_delivery_method'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.invoice_style) {
        ctx.addIssue({
          path: ['invoice_style'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.location_contact_email) {
        ctx.addIssue({
          path: ['location_contact_email'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      } else {
        const emailValidation = z.string().email({
          message: 'The email must be in xx@xx.xx format',
        });
        const result = emailValidation.safeParse(data.location_contact_email);

        if (!result.success) {
          ctx.addIssue({
            path: ['location_contact_email'],
            message: result.error.issues[0].message,
            code: z.ZodIssueCode.custom,
          });
        }
      }

      if (
        data.location_contact_email &&
        data.location_contact_email.length > 40
      ) {
        ctx.addIssue({
          path: ['location_contact_email'],
          message: 'Only 40 characters allowed',
          code: z.ZodIssueCode.custom,
        });
      }
    }

    if (data.needsShipping && !data.speed) {
      ctx.addIssue({
        path: ['speed'],
        message: 'Selecting a speed is required',
        code: z.ZodIssueCode.custom,
      });
    }
  });

export const AccessCheckoutFormSchema = z.object({
  locations: z.array(AddressSchema),
  order_name: z.string().transform(nullToUndefined).nullish(),
});

export type AccessCheckoutForm = z.infer<typeof AccessCheckoutFormSchema>;

export const AccessCheckoutRequestSchema = z.object({
  locations: z.array(
    z.object({
      address: AddressSchema._def.schema.extend({
        billing_type: z.string().transform(nullToUndefined).nullish(),
        invoice_style: z.string().transform(nullToUndefined).nullish(),
        invoice_delivery_method: z
          .string()
          .transform(nullToUndefined)
          .nullish(),
      }),

      order_items: z.any(),
    }),
  ),
  status: z.string(),
});

export type AccessCheckoutRequest = z.infer<typeof AccessCheckoutRequestSchema>;

export const AccessReviewOrderFormSchema = z.object({
  order_name: z.string().trim().nonempty('Required'),
});

export type AccessReviewOrderForm = z.infer<typeof AccessReviewOrderFormSchema>;
